/**
 * Machine types of the equipment catalog (`/reference-items/equipment`).
 * BOM operations reference a machine *type*, not one physical machine, so the
 * editor offers exactly the types the catalog already uses.
 */

/** Same catalogue as `EquipmentCrudPage` (`MACHINE_TYPE_CODES`). */
export const MACHINE_TYPE_CODES = [
  'single_cylinder',
  'double_cylinder',
  'computerized_single_cylinder',
  'computerized_double_cylinder',
  'linked_toe',
  'seamless',
  'flat_knitting',
  'hosiery',
  'other',
] as const

export type MachineTypeCode = (typeof MACHINE_TYPE_CODES)[number]

export function isMachineTypeCode(value: string | null | undefined): boolean {
  const code = (value ?? '').trim()
  return MACHINE_TYPE_CODES.includes(code as MachineTypeCode)
}

/** Localised label; a legacy free-text machine name is returned unchanged. */
export function machineTypeLabel(
  value: string | null | undefined,
  translate: (key: string) => string,
): string {
  const code = (value ?? '').trim()
  if (!code) return ''
  if (!isMachineTypeCode(code)) return code
  const key = `engineering.equipment.machineTypeOptions.${code}`
  const label = translate(key)
  return label === key ? code : label
}

interface MachineTypeSource {
  machine_type?: string | null
}

/**
 * Distinct machine types used by the equipment catalog, plus any value already
 * stored on an operation (a legacy machine name must stay selectable).
 */
export function machineTypeValues(
  equipment: MachineTypeSource[],
  currentValues: Array<string | null | undefined> = [],
): string[] {
  const seen = new Set<string>()
  const values: string[] = []
  const push = (raw?: string | null): void => {
    const value = (raw ?? '').trim()
    const key = value.toLocaleLowerCase()
    if (!value || seen.has(key)) return
    seen.add(key)
    values.push(value)
  }
  equipment.forEach((item) => push(item.machine_type))
  currentValues.forEach(push)
  return values
}

/** Option list for the operations table, sorted by the localised label. */
export function machineTypeOptions(
  equipment: MachineTypeSource[],
  currentValues: Array<string | null | undefined>,
  translate: (key: string) => string,
): string[] {
  return machineTypeValues(equipment, currentValues)
    .sort((left, right) => machineTypeLabel(left, translate).localeCompare(machineTypeLabel(right, translate)))
}
